/**
 * The Integrations hub — one frame, three tabs.
 *
 * Connected, Roles and Add connection are three views of one subject: the
 * systems this tenant reaches. As separate left-nav rows they made the sidebar
 * carry the hub's internal structure, and the three sat as siblings of things
 * like the audit log, which they are not.
 *
 * **Each tab keeps its own URL** — `/integration/connections`,
 * `/integration/bindings`, `/integration/available`. That is what tabs usually
 * cost you: the choice happens a click later and is invisible to the address
 * bar. Here it is not, so a deep link still lands on the right tab, the back
 * button steps between them, and a refresh stays put.
 *
 * No "Sync" — connections are tested, not synced (ingestion is a later slice).
 */

import { Stack, Tabs, Text, Title } from "@mantine/core";
import { IconApps, IconPlugConnected, IconTag } from "@tabler/icons-react";
import { useLocation, useNavigate } from "react-router-dom";

import { useEffectiveRoles } from "@/features/auth/RolePreviewContext";
import { useMe } from "@/features/users/useMe";
import { AvailableList } from "./AvailableList";
import { ConnectionBindings } from "./ConnectionBindings";
import { ConnectionsList } from "./ConnectionsList";

export type IntegrationsTab = "connected" | "roles" | "available";

const PATH_OF: Record<IntegrationsTab, string> = {
  connected: "/integration/connections",
  roles: "/integration/bindings",
  available: "/integration/available",
};

const BLURB: Record<IntegrationsTab, string> = {
  connected: "The external systems this tenant is configured to reach.",
  roles: "Names a pipeline can use instead of a connection id, bound per deployment.",
  available: "Pick a source type to connect. Configuring it opens its create form.",
};

export function IntegrationsHub({ tab }: { tab: IntegrationsTab }) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  // Connected and Add connection are Developer+ routes; Roles is not, and
  // tabbing them together must not quietly change that. A `User` who deep-links
  // to Roles gets Roles, with no tabs to pages the router would refuse — an
  // offered tab that 403s is worse than an absent one.
  const { data } = useMe();
  const roles = useEffectiveRoles(data?.roles ?? []);
  const isDeveloperPlus = roles.includes("developer") || roles.includes("administrator");

  return (
    <Stack gap="md">
      <div>
        <Title order={2}>Integrations</Title>
        <Text c="dimmed" size="sm">
          {BLURB[tab]}
        </Text>
      </div>

      <Tabs
        value={tab}
        onChange={(next) => {
          const to = PATH_OF[(next ?? "connected") as IntegrationsTab];
          if (to !== pathname) navigate(to);
        }}
        // Each tab fetches its own list; mounting all three would fire three
        // requests to open one.
        keepMounted={false}
      >
        <Tabs.List>
          {isDeveloperPlus && (
            <Tabs.Tab value="connected" leftSection={<IconPlugConnected size={16} />}>
              Connected
            </Tabs.Tab>
          )}
          <Tabs.Tab value="roles" leftSection={<IconTag size={16} />}>
            Roles
          </Tabs.Tab>
          {isDeveloperPlus && (
            <Tabs.Tab value="available" leftSection={<IconApps size={16} />}>
              Add connection
            </Tabs.Tab>
          )}
        </Tabs.List>

        <Tabs.Panel value="connected" pt="md">
          <ConnectionsList />
        </Tabs.Panel>
        <Tabs.Panel value="roles" pt="md">
          <ConnectionBindings />
        </Tabs.Panel>
        <Tabs.Panel value="available" pt="md">
          <AvailableList />
        </Tabs.Panel>
      </Tabs>
    </Stack>
  );
}
